/**
 * childrenStorage — persist the list of child profiles in localStorage.
 * Supports multiple children per parent account (or multiple children for a guest).
 */

import type { ChildProfile } from '../types'

const STORAGE_KEY_CHILDREN = 'energymatch-children'
const STORAGE_KEY_ACTIVE_CHILD = 'energymatch-active-child-id'

function isValidChild(obj: unknown): obj is ChildProfile {
  if (!obj || typeof obj !== 'object') return false
  const p = obj as Record<string, unknown>
  if (typeof p.id !== 'string' || !p.id) return false
  if (typeof p.firstName !== 'string') return false
  if (typeof p.age !== 'string') return false
  if (p.sex !== '' && p.sex !== 'male' && p.sex !== 'female') return false
  if (typeof p.weightKg !== 'string') return false
  if (!Array.isArray(p.dietaryRequirements)) return false
  if (p.dietaryRequirements.some((x) => typeof x !== 'string')) return false
  if (typeof p.dietaryOther !== 'string') return false
  return true
}

export function getStoredChildren(): ChildProfile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CHILDREN)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidChild)
  } catch {
    return []
  }
}

export function setStoredChildren(children: ChildProfile[]): void {
  try {
    localStorage.setItem(STORAGE_KEY_CHILDREN, JSON.stringify(children))
  } catch {
    // quota or disabled
  }
}

export function getActiveChildId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_ACTIVE_CHILD)
  } catch {
    return null
  }
}

export function setActiveChildId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_ACTIVE_CHILD, id)
  } catch {
    // ignore
  }
}

/** Add a new child profile (generates a UUID if not present). Returns the saved profile. */
export function addStoredChild(profile: Omit<ChildProfile, 'id'> & { id?: string }): ChildProfile {
  const child: ChildProfile = { ...profile, id: profile.id ?? crypto.randomUUID() }
  const children = getStoredChildren()
  setStoredChildren([...children, child])
  return child
}

/** Replace a child profile by id. */
export function updateStoredChild(updated: ChildProfile): void {
  const children = getStoredChildren()
  setStoredChildren(children.map((c) => (c.id === updated.id ? updated : c)))
}

/** Remove a child profile and their namespaced data by id. */
export function removeStoredChild(id: string): void {
  const children = getStoredChildren().filter((c) => c.id !== id)
  setStoredChildren(children)
  // Clear all per-child localStorage data
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.endsWith(`-${id}`)) keysToRemove.push(key)
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k))
  // Clear active child if it was the removed one
  if (getActiveChildId() === id) localStorage.removeItem(STORAGE_KEY_ACTIVE_CHILD)
}
